import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useEffect,
} from "react";
import { Booking, BookingSlot, TimeSlot } from "../types/booking";
import { Customer } from "../types/user";
import { updateSessionData, getSessionData } from "../utils/cookies";
import { analytics } from "../utils/analytics";

export interface UploadedFile {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
  dataUrl?: string;
  uploadStatus: "pending" | "uploading" | "uploaded" | "error";
  uploadProgress?: number;
  error?: string;
  s3Url?: string;
  timestamp: number;
}

interface BookingState {
  customer: Customer | null;
  currentBooking: Partial<Booking> | null;
  availableSlots: BookingSlot[];
  selectedDate: string | null;
  selectedTimeSlot: TimeSlot | null;
  serviceType: "onsite" | "phone_consultation" | "service_callout" | null;
  currentStep:
    | "contact"
    | "measurement"
    | "estimate"
    | "booking"
    | "confirmation";
  loading: boolean;
  error: string | null;
  uploadedFiles: UploadedFile[];
}

type BookingAction =
  | { type: "SET_CUSTOMER"; payload: Customer }
  | { type: "SET_BOOKING_DATA"; payload: Partial<Booking> }
  | { type: "SET_AVAILABLE_SLOTS"; payload: BookingSlot[] }
  | { type: "SET_SELECTED_DATE"; payload: string | null }
  | { type: "SET_SELECTED_TIME_SLOT"; payload: TimeSlot | null }
  | {
      type: "SET_SERVICE_TYPE";
      payload: "onsite" | "phone_consultation" | "service_callout" | null;
    }
  | { type: "SET_CURRENT_STEP"; payload: BookingState["currentStep"] }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "ADD_UPLOADED_FILE"; payload: UploadedFile }
  | {
      type: "UPDATE_FILE_STATUS";
      payload: {
        id: string;
        status: UploadedFile["uploadStatus"];
        progress?: number;
        error?: string;
        s3Url?: string;
      };
    }
  | { type: "REMOVE_UPLOADED_FILE"; payload: string }
  | { type: "CLEAR_UPLOADED_FILES" }
  | { type: "RESET_BOOKING" };

const initialState: BookingState = {
  customer: null,
  currentBooking: null,
  availableSlots: [],
  selectedDate: null,
  selectedTimeSlot: null,
  serviceType: null,
  currentStep: "contact",
  loading: false,
  error: null,
  uploadedFiles: [],
};

const bookingReducer = (
  state: BookingState,
  action: BookingAction
): BookingState => {
  switch (action.type) {
    case "SET_CUSTOMER":
      return { ...state, customer: action.payload };
    case "SET_BOOKING_DATA":
      return {
        ...state,
        currentBooking: { ...state.currentBooking, ...action.payload },
      };
    case "SET_AVAILABLE_SLOTS":
      return { ...state, availableSlots: action.payload };
    case "SET_SELECTED_DATE":
      return { ...state, selectedDate: action.payload };
    case "SET_SELECTED_TIME_SLOT":
      return { ...state, selectedTimeSlot: action.payload };
    case "SET_SERVICE_TYPE":
      return { ...state, serviceType: action.payload };
    case "SET_CURRENT_STEP":
      return { ...state, currentStep: action.payload };
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    case "ADD_UPLOADED_FILE":
      return {
        ...state,
        uploadedFiles: [...state.uploadedFiles, action.payload],
      };
    case "UPDATE_FILE_STATUS":
      return {
        ...state,
        uploadedFiles: state.uploadedFiles.map((file) =>
          file.id === action.payload.id
            ? {
                ...file,
                uploadStatus: action.payload.status,
                uploadProgress: action.payload.progress,
                error: action.payload.error,
                s3Url: action.payload.s3Url,
              }
            : file
        ),
      };
    case "REMOVE_UPLOADED_FILE":
      return {
        ...state,
        uploadedFiles: state.uploadedFiles.filter(
          (file) => file.id !== action.payload
        ),
      };
    case "CLEAR_UPLOADED_FILES":
      return { ...state, uploadedFiles: [] };
    case "RESET_BOOKING":
      return initialState;
    default:
      return state;
  }
};

interface BookingContextType {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  bookingData: Partial<Booking>;
  updateBookingData: (data: Partial<Booking>) => void;
  setCustomer: (customer: Customer) => void;
  setBookingData: (data: Partial<Booking>) => void;
  setAvailableSlots: (slots: BookingSlot[]) => void;
  setSelectedDate: (date: string | null) => void;
  setSelectedTimeSlot: (slot: TimeSlot | null) => void;
  setServiceType: (
    serviceType: "onsite" | "phone_consultation" | "service_callout" | null
  ) => void;
  setCurrentStep: (step: BookingState["currentStep"]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  resetBooking: () => void;
  // File management methods
  addUploadedFile: (file: File) => string;
  updateFileStatus: (
    id: string,
    status: UploadedFile["uploadStatus"],
    progress?: number,
    error?: string,
    s3Url?: string
  ) => void;
  removeUploadedFile: (id: string) => void;
  clearUploadedFiles: () => void;
  uploadedFiles: UploadedFile[];
}

const BookingContext = createContext<BookingContextType | undefined>(undefined);

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error("useBooking must be used within a BookingProvider");
  }
  return context;
};

interface BookingProviderProps {
  children: ReactNode;
}

export const BookingProvider: React.FC<BookingProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(bookingReducer, initialState);

  // Initialize booking data from session storage on mount
  useEffect(() => {
    const sessionData = getSessionData();
    if (sessionData?.bookingData) {
      console.log(
        "🔍 DEBUG: BookingContext - Initializing from session storage:",
        sessionData.bookingData
      );
      dispatch({ type: "SET_BOOKING_DATA", payload: sessionData.bookingData });
    }
    if (sessionData?.customerData) {
      console.log(
        "🔍 DEBUG: BookingContext - Initializing customer from session storage:",
        sessionData.customerData
      );
      dispatch({ type: "SET_CUSTOMER", payload: sessionData.customerData });
    }
  }, []);

  const setCustomer = (customer: Customer) => {
    dispatch({ type: "SET_CUSTOMER", payload: customer });
  };

  const setBookingData = (data: Partial<Booking>) => {
    dispatch({ type: "SET_BOOKING_DATA", payload: data });
  };

  const updateBookingData = (data: Partial<Booking>) => {
    dispatch({ type: "SET_BOOKING_DATA", payload: data });

    // Sync with session storage to persist price breakdown and other booking data
    updateSessionData({
      bookingData: {
        ...state.currentBooking,
        ...data,
      },
    });
  };

  const bookingData = state.currentBooking || {};

  const setAvailableSlots = (slots: BookingSlot[]) => {
    dispatch({ type: "SET_AVAILABLE_SLOTS", payload: slots });
  };

  const setSelectedDate = (date: string | null) => {
    dispatch({ type: "SET_SELECTED_DATE", payload: date });
  };

  const setSelectedTimeSlot = (slot: TimeSlot | null) => {
    dispatch({ type: "SET_SELECTED_TIME_SLOT", payload: slot });
  };
  const setServiceType = (
    serviceType: "onsite" | "phone_consultation" | "service_callout" | null
  ) => {
    dispatch({ type: "SET_SERVICE_TYPE", payload: serviceType });
    
    // Track service type selection with enhanced GA4 analytics
    if (serviceType) {
      analytics.trackServiceTypeSelection(serviceType);
    }
  };

  const setCurrentStep = (step: BookingState["currentStep"]) => {
    dispatch({ type: "SET_CURRENT_STEP", payload: step });
  };

  const setLoading = (loading: boolean) => {
    dispatch({ type: "SET_LOADING", payload: loading });
  };

  const setError = (error: string | null) => {
    dispatch({ type: "SET_ERROR", payload: error });
  };

  const resetBooking = () => {
    dispatch({ type: "RESET_BOOKING" });
  };

  // File management methods
  const addUploadedFile = (file: File): string => {
    const id = `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const uploadedFile: UploadedFile = {
      id,
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      uploadStatus: "pending",
      timestamp: Date.now(),
    };
    dispatch({ type: "ADD_UPLOADED_FILE", payload: uploadedFile });
    return id;
  };

  const updateFileStatus = (
    id: string,
    status: UploadedFile["uploadStatus"],
    progress?: number,
    error?: string,
    s3Url?: string
  ) => {
    dispatch({
      type: "UPDATE_FILE_STATUS",
      payload: { id, status, progress, error, s3Url },
    });
  };

  const removeUploadedFile = (id: string) => {
    dispatch({ type: "REMOVE_UPLOADED_FILE", payload: id });
  };

  const clearUploadedFiles = () => {
    dispatch({ type: "CLEAR_UPLOADED_FILES" });
  };

  return (
    <BookingContext.Provider
      value={{
        state,
        dispatch,
        bookingData,
        updateBookingData,
        setCustomer,
        setBookingData,
        setAvailableSlots,
        setSelectedDate,
        setSelectedTimeSlot,
        setServiceType,
        setCurrentStep,
        setLoading,
        setError,
        resetBooking,
        addUploadedFile,
        updateFileStatus,
        removeUploadedFile,
        clearUploadedFiles,
        uploadedFiles: state.uploadedFiles,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
};
